/**
 * Cost band for paid AI buttons that did not already have a confirm
 * (#1223). Planning math only — an estimate, never a bill, never a cap.
 *
 * Rates join the OpenRouter catalog the same way as Create Outline,
 * Plot Grid Generate, and Write draft. Output ceilings follow the
 * call that button actually makes. Input is the live thread size when
 * the caller passes it (chat). Otherwise the plan's planning input is
 * a typical prompt, not a measured one.
 *
 * typical = calls × (I × Rp + typicalOutput × Rc)
 * worst   = calls × (I × Rp + worstOutput × Rc)
 */

import {
  formatEstimateAbout,
  formatEstimateRange,
  formatEstimateUsd,
  pricingFromCatalog,
} from './estimateOutlineCost';

export const CHAT_DEFAULT_MODEL = 'anthropic/claude-sonnet-5';
export const SONNET_5 = 'anthropic/claude-sonnet-5';
export const HAIKU = 'anthropic/claude-haiku-4.5';
export const GPT_MINI = 'openai/gpt-4o-mini';
export const FRAMEWORK_BUILDER_MODEL = 'google/gemini-3.8-flash';

/** llm.chat_stream default max_tokens. The writer-facing band does not use this ceiling. */
export const CHAT_OUTPUT_BUDGET = 4096;

/**
 * A typical reply on staging is about 50–200 tokens (one line) and the
 * median across chat calls is about 260. The high end follows short-thread
 * replies, not the 4,096 ceiling.
 */
export const CHAT_REPLY_TYPICAL_OUTPUT = 200;
export const CHAT_REPLY_WORST_OUTPUT = 600;

/** System prompt is not in the loaded thread. A one-line reply measured about 3,400–3,550 input tokens. */
export const CHAT_SYSTEM_PAD_TOKENS = 3000;

/** Automatic summary: every 15 saved messages. Classifiers stay off in the opening window. */
export const SUMMARY_EVERY_MESSAGES = 15;
export const CLASSIFIER_MIN_MESSAGES = 4;

/** Span and gist run to this budget: 1,500, plus 25 per message past 15, capped at 4,096. */
export const SUMMARY_BASE_TOKENS = 1500;
export const SUMMARY_TOKENS_PER_MESSAGE = 25;
export const SUMMARY_OUTPUT_CEILING = 4096;

/**
 * segment_message_to_cards sends ADD_TO_BIBLE_SEGMENTATION_PROMPT
 * (1,464 characters, 366 tokens) plus the chosen message, and caps
 * the reply at 2,048 tokens.
 */
export const SEGMENT_MESSAGE_PROMPT_TOKENS = 366;

export function segmentMessageInputTokens(content) {
  const message = Math.ceil(String(content || '').length / 4);
  return SEGMENT_MESSAGE_PROMPT_TOKENS + message;
}

export const CANT_ESTIMATE_RATES_LINE =
  "Can't estimate — OpenRouter rates didn't load. You can still confirm.";

export const CANT_ESTIMATE_SIZE_LINE =
  "Can't estimate — prompt size didn't load. You can still confirm.";

export const ESTIMATE_NOT_EXACT = 'Not an exact cost.';

export const PAID_ACTIONS = {
  jump_start: {
    title: 'Jump Start',
    hint: 'One prompt from this chat. Cancel spends nothing.',
    functionKey: 'chat',
    defaultModelId: CHAT_DEFAULT_MODEL,
    planningInputTokens: 2000,
    typicalOutput: CHAT_REPLY_TYPICAL_OUTPUT,
    worstOutput: CHAT_REPLY_WORST_OUTPUT,
  },
  nudge: {
    title: 'Nudge',
    hint: 'A few directions from this chat. Cancel spends nothing.',
    functionKey: 'chat',
    defaultModelId: CHAT_DEFAULT_MODEL,
    planningInputTokens: 2000,
    typicalOutput: 400,
    worstOutput: 900,
  },
  chat_message: {
    title: 'Send',
    hint: 'This message and the reply. Cancel spends nothing.',
    functionKey: 'chat',
    defaultModelId: CHAT_DEFAULT_MODEL,
    planningInputTokens: 2000,
    typicalOutput: CHAT_REPLY_TYPICAL_OUTPUT,
    worstOutput: CHAT_REPLY_WORST_OUTPUT,
  },
  regenerate: {
    title: 'Regenerate',
    hint: 'Writes this reply again. Cancel spends nothing.',
    functionKey: 'chat',
    defaultModelId: CHAT_DEFAULT_MODEL,
    planningInputTokens: 4000,
    typicalOutput: CHAT_REPLY_TYPICAL_OUTPUT,
    worstOutput: CHAT_REPLY_WORST_OUTPUT,
  },
  check_coverage: {
    title: 'Check coverage',
    hint: 'Marks which sequences this chat has covered. Cancel spends nothing.',
    functionKey: 'summary',
    defaultModelId: GPT_MINI,
    planningInputTokens: 2500,
    typicalOutput: 400,
    worstOutput: 800,
  },
  scene_continue: {
    title: 'Continue this draft',
    hint: 'Adds to this draft, including the check that runs after it. Cancel writes nothing.',
    functionKey: 'prose_scene_generation',
    defaultModelId: SONNET_5,
    // The confirm uses the measured prompt. This number is only a spare.
    planningInputTokens: 4000,
    // Staging scene drafts: median output about 1,370, 90th about 2,700.
    // The continue cap is still 8,192.
    typicalOutput: 1400,
    worstOutput: 2800,
  },
  plot_continue: {
    title: 'Continue',
    hint: 'One more pass on this outline. Cancel spends nothing.',
    functionKey: 'story_board_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 4000,
    // Staging outline calls: median output about 1,600, 90th about 6,000.
    // A fills-spine continue can still ask for 16,000.
    typicalOutput: 2000,
    worstOutput: 6000,
  },
  sequence_check: {
    title: 'Check my sequences',
    hint: "Reads each sequence's Plot Focus chat and says which still need a decision, with suggestions you can ignore. Cancel spends nothing.",
    functionKey: 'story_board_generation',
    defaultModelId: SONNET_5,
    // Jev grades each sequence for a fraction of a cent. The model writes
    // one suggestion (at most 700 tokens) only for sequences that need one.
    // Five is a handful of those suggestions, not one giant prompt.
    planningInputTokens: 1500,
    typicalOutput: 250,
    worstOutput: 700,
    calls: 5,
  },
  sequence_check_one: {
    title: 'Check this sequence',
    hint: "Reads this sequence's chat and says whether it decides what happens, with a suggestion you can ignore. Cancel spends nothing.",
    functionKey: 'story_board_generation',
    defaultModelId: SONNET_5,
    // One sequence: Jev grades it; the model writes one suggestion if needed.
    planningInputTokens: 1500,
    typicalOutput: 250,
    worstOutput: 700,
  },
  check_outline: {
    title: 'Check outline',
    hint: 'Reads this outline and reports what does not fit. Cancel spends nothing.',
    functionKey: 'check_outline',
    defaultModelId: 'typesafe/jev-1.13',
    planningInputTokens: 3000,
    typicalOutput: 1500,
    worstOutput: 4000,
  },
  theme: {
    title: 'Generate theme',
    hint: 'Writes a short theme line. Cancel spends nothing.',
    functionKey: 'chat',
    defaultModelId: CHAT_DEFAULT_MODEL,
    planningInputTokens: 2000,
    typicalOutput: 40,
    worstOutput: 100,
  },
  logline: {
    title: 'Generate logline',
    hint: 'Writes a logline from this chat. Cancel spends nothing.',
    functionKey: 'chat',
    defaultModelId: CHAT_DEFAULT_MODEL,
    planningInputTokens: 2500,
    typicalOutput: 200,
    worstOutput: 600,
  },
  character_note: {
    title: 'Character note',
    hint: 'Proposes a note from your sessions. Cancel spends nothing.',
    functionKey: 'character_dossier',
    defaultModelId: SONNET_5,
    // Staging character notes: about 600 input tokens and 360 output tokens.
    planningInputTokens: 800,
    typicalOutput: 400,
    worstOutput: 800,
  },
  plot_extract: {
    title: 'Bring a turn',
    hint: 'Proposes plot turns from this book. Cancel spends nothing.',
    functionKey: 'plot_dossier',
    defaultModelId: SONNET_5,
    // Same call shape as a character note (cap 2,000). Staging had no plot rows.
    planningInputTokens: 800,
    typicalOutput: 400,
    worstOutput: 800,
  },
  style_voice: {
    title: 'Generate voice',
    hint: 'Writes a voice instruction from the Character note. Cancel spends nothing.',
    functionKey: 'style_instruction',
    defaultModelId: HAIKU,
    // Staging voice call: about 880 input tokens and 150 output tokens. Cap is 800.
    planningInputTokens: 900,
    typicalOutput: 150,
    worstOutput: 400,
  },
  framework_builder: {
    title: 'Build with help',
    hint: 'Brainstormy reads this conversation and replies, and may suggest sequences. Nothing is saved until you save the Framework. Cancel spends nothing.',
    functionKey: 'framework_builder',
    defaultModelId: FRAMEWORK_BUILDER_MODEL,
    planningInputTokens: 4500,
    // The model thinks before it answers (about 900 tokens on a proposal), billed as output.
    typicalOutput: 1200,
    // A full 4,000-token reply; the second call when the genre is first named is rarer.
    worstOutput: 4000,
  },
  scene_title: {
    title: 'Suggest title',
    hint: 'Reads what happens in this scene and suggests a short title. You can change it. Cancel spends nothing.',
    functionKey: 'bible_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 400,
    typicalOutput: 15,
    worstOutput: 40,
  },
  book_cast: {
    title: 'Find your cast',
    hint: 'Reads what you said about this book and lists its people and places. Writes nothing until you add them. Cancel spends nothing.',
    functionKey: 'bible_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 3000,
    typicalOutput: 600,
    worstOutput: 1500,
  },
  bible_card: {
    title: 'Generate note',
    hint: 'Writes this Book Note. Cancel spends nothing.',
    functionKey: 'bible_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 2500,
    typicalOutput: 1000,
    worstOutput: 2048,
  },
  note_redraft: {
    title: 'Redraft',
    hint: "Brainstormy rewrites this Note from what's in your book now. You choose whether to keep it.",
    functionKey: 'bible_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 2500,
    typicalOutput: 800,
    worstOutput: 1600,
    calls: 1,
  },
  bible_both: {
    title: 'Generate theme and logline',
    hint: 'Two notes: theme, then logline. Cancel spends nothing.',
    functionKey: 'bible_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 2500,
    typicalOutput: 1000,
    worstOutput: 2048,
    calls: 2,
  },
  series_fill: {
    title: 'Fill empty notes',
    hint: 'Proposes the empty Series Notes. Cancel spends nothing.',
    functionKey: 'series_bible_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 4000,
    typicalOutput: 2000,
    worstOutput: 4096,
  },
  series_element: {
    title: 'Generate this note',
    hint: 'Proposes this Series Note. Cancel spends nothing.',
    functionKey: 'series_bible_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 2000,
    typicalOutput: 200,
    worstOutput: 400,
  },
  series_regenerate: {
    title: 'Regenerate Series Notes',
    hint: 'Rewrites freeform notes. Core notes stay. Cancel spends nothing.',
    functionKey: 'series_bible_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 4000,
    typicalOutput: 2000,
    worstOutput: 4096,
    calls: 3,
  },
  outline_all: {
    title: 'Generate all',
    hint: 'Writes the empty sections. Cancel spends nothing.',
    functionKey: 'story_board_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 2000,
    typicalOutput: 1500,
    worstOutput: 3000,
    calls: 4,
  },
  outline_section: {
    title: 'Generate section',
    hint: 'Writes this section. Cancel spends nothing.',
    functionKey: 'story_board_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 1800,
    typicalOutput: 800,
    worstOutput: 1600,
  },
  generate_sequences: {
    title: 'Generate sequences',
    hint: 'Proposes sequences for this act. Cancel spends nothing.',
    functionKey: 'story_board_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 1800,
    typicalOutput: 700,
    worstOutput: 1400,
  },
  generate_scenes: {
    title: 'Generate scenes',
    hint: 'Proposes scenes for this sequence. Cancel spends nothing.',
    functionKey: 'story_board_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 1800,
    typicalOutput: 800,
    worstOutput: 1600,
  },
  scene_fields: {
    title: 'Regenerate scene fields',
    hint: 'Proposes fields from linked chats. Cancel spends nothing.',
    functionKey: 'summary',
    defaultModelId: GPT_MINI,
    planningInputTokens: 2500,
    typicalOutput: 450,
    worstOutput: 900,
  },
  get_ideas: {
    title: 'Get book ideas',
    hint: 'Proposes book ideas from this series. Cancel spends nothing.',
    functionKey: 'bible_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 4000,
    typicalOutput: 1200,
    worstOutput: 2200,
    calls: 2,
  },
  docs_notes: {
    title: 'Make notes from these docs',
    hint: 'Proposes character notes from the docs you checked. Cancel spends nothing.',
    functionKey: 'bible_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 4000,
    typicalOutput: 1800,
    worstOutput: 3500,
  },
  import_digest: {
    title: 'Read these docs',
    hint: 'Reads the upload and writes a short digest. Cancel spends nothing.',
    functionKey: 'summary',
    defaultModelId: GPT_MINI,
    planningInputTokens: 4000,
    typicalOutput: 1000,
    worstOutput: 2048,
  },
  create_summary: {
    title: 'Create summary',
    hint: 'Writes a summary of this chat. Cancel spends nothing.',
    functionKey: 'chat',
    defaultModelId: CHAT_DEFAULT_MODEL,
    planningInputTokens: 4000,
    typicalOutput: 800,
    worstOutput: 2000,
  },
  editing_regenerate: {
    title: 'Regenerate scene',
    hint: 'Writes the scene again from your rulings. Cancel spends nothing.',
    functionKey: 'prose_scene_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 4000,
    typicalOutput: 2000,
    worstOutput: 4096,
  },
  add_book_notes: {
    title: 'Add to Book Notes',
    hint: 'Adds the notes you chose. Cancel adds nothing.',
    functionKey: 'bible_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 800,
    typicalOutput: 200,
    worstOutput: 600,
  },
  add_message_to_notes: {
    title: 'Add to Book Notes',
    hint: 'Reads this message and proposes notes. Cancel adds nothing.',
    functionKey: 'bible_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: 1500,
    typicalOutput: 400,
    worstOutput: 1200,
  },
  segment_message: {
    title: 'Add to Book Notes',
    hint: 'Reads this message and proposes notes. Cancel adds nothing.',
    functionKey: 'bible_generation',
    defaultModelId: SONNET_5,
    planningInputTokens: SEGMENT_MESSAGE_PROMPT_TOKENS,
    typicalOutput: 400,
    worstOutput: 2048,
  },
};

export function threadInputTokens(messages, pad = CHAT_SYSTEM_PAD_TOKENS) {
  const chars = (messages || []).reduce(
    (sum, message) => sum + String(message?.content || '').length,
    0,
  );
  return Math.floor(chars / 4) + pad;
}

export function resolvePaidModelId(modelPreferences, action) {
  const saved = modelPreferences?.[action.functionKey];
  const text = typeof saved === 'string' ? saved.trim() : '';
  return text || action.defaultModelId;
}

export function estimatePaidActionCostUsd(modelPricing, action, inputTokens) {
  const prompt = Number(modelPricing?.prompt);
  const completion = Number(modelPricing?.completion);
  if (!Number.isFinite(prompt) || !Number.isFinite(completion)) return null;
  const input = Number(inputTokens);
  if (!Number.isFinite(input) || input < 0) return null;
  const calls = action.calls || 1;
  const call = (output) => calls * (input * prompt + output * completion);
  return {
    typical: call(action.typicalOutput),
    worst: call(action.worstOutput),
    inputTokens: input,
  };
}

export function formatPaidEstimateAmount(band) {
  if (!band || !Number.isFinite(band.typical) || !Number.isFinite(band.worst)) return null;
  return formatEstimateRange(band.typical, band.worst);
}

export function formatPaidEstimateLine(band) {
  const amount = formatPaidEstimateAmount(band);
  if (!amount) return null;
  return `Estimate: ${formatEstimateAbout(amount)} for this run. ${ESTIMATE_NOT_EXACT}`;
}

/** Writer-facing. Does not name the checks. */
export const CHAT_SEND_CHECKS_LINE = 'Includes the reply and the checks that run with it.';

export const CHAT_SEND_SUMMARY_LINE = 'Includes the reply and the summary this send starts.';

export const CHAT_SEND_SUMMARY_AND_CHECKS_LINE =
  'Includes the reply, the checks that run with it, and the summary this send starts.';

const REPLY_PART = {
  id: 'reply',
  functionKey: 'chat',
  defaultModelId: CHAT_DEFAULT_MODEL,
  typicalOutput: CHAT_REPLY_TYPICAL_OUTPUT,
  worstOutput: CHAT_REPLY_WORST_OUTPUT,
  calls: 1,
  useThreadInput: true,
};

const CLASSIFIER_PART = {
  id: 'classifier',
  functionKey: 'cpa_classifier',
  defaultModelId: HAIKU,
  planningInputTokens: 1400,
  typicalOutput: 40,
  worstOutput: 200,
  calls: 2,
};

/** Foundation read. One staging read wrote 486 output tokens. Priced on its own. */
const FOUNDATION_PART = {
  id: 'foundation',
  functionKey: 'cpa_classifier',
  defaultModelId: HAIKU,
  planningInputTokens: 1400,
  typicalOutput: 500,
  worstOutput: 500,
  calls: 1,
};

/** Two checks (span and gist). Staging median about 3,200 in and 1,600 out. */
const CLAIM_PART = {
  id: 'claim_checks',
  functionKey: 'claim_verification',
  defaultModelId: SONNET_5,
  planningInputTokens: 3200,
  typicalOutput: 1600,
  worstOutput: 2400,
  calls: 2,
};

/** Discovery summaries run on the chat model: one span, then one gist. Output is the budget. */
const SUMMARY_SPAN_PART = {
  id: 'summary_span',
  functionKey: 'chat',
  defaultModelId: CHAT_DEFAULT_MODEL,
  planningInputTokens: 2500,
  calls: 1,
  useReplyModel: true,
};

const SUMMARY_GIST_PART = {
  id: 'summary_gist',
  functionKey: 'chat',
  defaultModelId: CHAT_DEFAULT_MODEL,
  planningInputTokens: 2000,
  calls: 1,
  useReplyModel: true,
};

const SUMMARY_MINI_PART = {
  id: 'summary',
  functionKey: 'summary',
  defaultModelId: GPT_MINI,
  planningInputTokens: 2100,
  typicalOutput: 400,
  worstOutput: 700,
  calls: 1,
};

/**
 * Output budget for a span or a gist at this message count.
 * 1,500 at the first summary, then 25 more per message past 15, up to 4,096.
 */
export function summaryOutputBudget(messageCount) {
  const count = Number(messageCount);
  const n = Number.isFinite(count) && count >= 0 ? count : SUMMARY_EVERY_MESSAGES;
  const extra = Math.max(0, n - SUMMARY_EVERY_MESSAGES) * SUMMARY_TOKENS_PER_MESSAGE;
  return Math.min(SUMMARY_BASE_TOKENS + extra, SUMMARY_OUTPUT_CEILING);
}

/**
 * A Send saves the writer's line and the reply. A summary starts when that
 * pair reaches the next 15 messages since the last summary. When
 * lastSummaryAt is omitted, earlier summaries are assumed to have landed
 * on that schedule. messagesAdded 0 is a replace-in-place (Regenerate).
 */
export function sendTriggersSummary(messageCount, {
  messagesAdded = 2,
  lastSummaryAt = null,
} = {}) {
  const count = Number(messageCount);
  if (!Number.isFinite(count) || count < 0) return false;
  const added = Number(messagesAdded);
  const step = Number.isFinite(added) && added >= 0 ? added : 2;
  const assumed = Math.floor(count / SUMMARY_EVERY_MESSAGES) * SUMMARY_EVERY_MESSAGES;
  const last = lastSummaryAt == null ? assumed : Number(lastSummaryAt);
  if (!Number.isFinite(last) || last < 0) return false;
  return (count - last) + step >= SUMMARY_EVERY_MESSAGES;
}

/**
 * Jump Start saves one assistant line and skips the Discovery classifiers.
 * Nudge saves a marker and a reply, and can run the same checks as Send.
 * Regenerate replaces the reply and does not start a summary.
 */
const CHAT_ACTION_PLANS = {
  chat_message: { messagesAdded: 2, classifiers: true, foundation: true },
  jump_start: { messagesAdded: 1, classifiers: false, foundation: false },
  nudge: { messagesAdded: 2, classifiers: true, foundation: true },
  regenerate: { messagesAdded: 0, classifiers: true, foundation: true },
};

export function isChatSessionAction(actionId) {
  return Object.prototype.hasOwnProperty.call(CHAT_ACTION_PLANS, actionId);
}

/**
 * Reply on every Send. Discovery classifiers after the opening window.
 * Summary and its two claim checks only on an action that will start a summary.
 * Span and gist claim checks still run with that summary. The separate
 * context-summary telemetry check is off, and it is not in this band.
 */
export function chatSendParts({
  messageCount = null,
  discovery = false,
  messagesAdded = 2,
  classifiers = true,
  foundation = true,
  reply = REPLY_PART,
} = {}) {
  const parts = [reply];
  const count = Number(messageCount);
  const known = Number.isFinite(count) && count >= 0;
  if (classifiers && discovery && known && count >= CLASSIFIER_MIN_MESSAGES) {
    parts.push(CLASSIFIER_PART);
    if (foundation) parts.push(FOUNDATION_PART);
  }
  if (known && sendTriggersSummary(count, { messagesAdded })) {
    if (discovery) {
      const added = Number(messagesAdded);
      const step = Number.isFinite(added) && added >= 0 ? added : 2;
      const budget = summaryOutputBudget(count + step);
      parts.push(
        { ...SUMMARY_SPAN_PART, typicalOutput: budget, worstOutput: budget },
        { ...SUMMARY_GIST_PART, typicalOutput: budget, worstOutput: budget },
      );
    } else {
      parts.push(SUMMARY_MINI_PART);
    }
    parts.push(CLAIM_PART);
  }
  return parts;
}

function partModelId(modelPreferences, part, replyModelId) {
  if ((part.useThreadInput || part.useReplyModel) && replyModelId) return replyModelId;
  const saved = modelPreferences?.[part.functionKey];
  const text = typeof saved === 'string' ? saved.trim() : '';
  return text || part.defaultModelId;
}

/**
 * @returns {{typical: number, worst: number, inputTokens: number, parts: object[]}|null}
 */
export function estimateChatActionUsd({
  actionId = 'chat_message',
  catalog,
  modelPreferences = null,
  inputTokens = null,
  replyModelId = null,
  messageCount = null,
  discovery = false,
} = {}) {
  const action = PAID_ACTIONS[actionId] || PAID_ACTIONS.chat_message;
  const plan = CHAT_ACTION_PLANS[actionId] || CHAT_ACTION_PLANS.chat_message;
  const reply = {
    ...REPLY_PART,
    typicalOutput: action.typicalOutput,
    worstOutput: action.worstOutput,
  };
  const threadSize = inputTokens != null ? inputTokens : action.planningInputTokens;
  let typical = 0;
  let worst = 0;
  const parts = [];
  for (const part of chatSendParts({
    messageCount,
    discovery,
    messagesAdded: plan.messagesAdded,
    classifiers: plan.classifiers,
    foundation: plan.foundation,
    reply,
  })) {
    const modelId = partModelId(modelPreferences, part, replyModelId);
    const pricing = pricingFromCatalog(catalog, modelId);
    const size = part.useThreadInput ? threadSize : part.planningInputTokens;
    const band = estimatePaidActionCostUsd(pricing, part, size);
    if (!band) return null;
    typical += band.typical;
    worst += band.worst;
    parts.push({ id: part.id, modelId, ...band });
  }
  return { typical, worst, inputTokens: Number(threadSize), parts };
}

export function estimateChatSendUsd(args = {}) {
  return estimateChatActionUsd({ ...args, actionId: 'chat_message' });
}

/**
 * Continue this draft: the prose call at the measured prompt, plus the
 * check that runs after it. A missing check rate means the whole run
 * cannot be priced.
 */
export function estimateContinueBand(prosePricing, inputTokens, check) {
  const prose = estimatePaidActionCostUsd(
    prosePricing,
    PAID_ACTIONS.scene_continue,
    inputTokens,
  );
  if (!prose) return null;
  if (!check) return prose;
  const extra = estimatePaidActionCostUsd(check.pricing, {
    typicalOutput: check.typicalOutput,
    worstOutput: check.worstOutput,
    calls: 1,
  }, check.inputTokens);
  if (!extra) return null;
  return {
    typical: prose.typical + extra.typical,
    worst: prose.worst + extra.worst,
    inputTokens: prose.inputTokens,
  };
}

export function formatChatSendEstimateLine(band) {
  const amount = formatPaidEstimateAmount(band);
  if (!amount) return null;
  const ids = new Set((band.parts || []).map((part) => part.id));
  const summary = ids.has('summary') || ids.has('summary_span') || ids.has('summary_gist');
  const checks = ids.has('classifier') || ids.has('foundation') || ids.has('claim_checks');
  let extra = '';
  if (summary && checks) extra = ` ${CHAT_SEND_SUMMARY_AND_CHECKS_LINE}`;
  else if (summary) extra = ` ${CHAT_SEND_SUMMARY_LINE}`;
  else if (checks) extra = ` ${CHAT_SEND_CHECKS_LINE}`;
  return `Estimate: ${formatEstimateAbout(amount)} for this run.${extra} ${ESTIMATE_NOT_EXACT}`;
}

export { formatEstimateUsd, pricingFromCatalog };
